import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AdminVoiceInsights } from "@/components/admin/AdminVoiceInsights";

const SPANS = [["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"], ["365d", "1 year"]];
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Card({ label, value, sub, testId }) {
  return (
    <div className="ss-card p-4" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-cocoa/50">{label}</p>
      <p className="mt-1 font-heading text-3xl text-cocoa">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-cocoa/55">{sub}</p>}
    </div>
  );
}

function Bars({ rows, valueKey = "n", labelKey = "day", color = "bg-gold" }) {
  const max = Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  if (!rows.length) return <p className="text-sm text-cocoa/50">No events in this window yet.</p>;
  return (
    <div className="flex items-end gap-1 h-28">
      {rows.map((r) => (
        <div key={r[labelKey]} className="flex-1 flex flex-col items-center justify-end" title={`${r[labelKey]}: ${r[valueKey]}`}>
          <div className={`w-full rounded-t ${color}`} style={{ height: `${Math.max(3, (100 * (r[valueKey] || 0)) / max)}%` }} />
        </div>
      ))}
    </div>
  );
}

export function AdminGrowth() {
  const [span, setSpan] = useState("30d");
  const [funnel, setFunnel] = useState(null);
  const [rev, setRev] = useState(null);
  const [ov, setOv] = useState(null);

  useEffect(() => {
    const days = { "7d": 7, "30d": 30, "90d": 90, "365d": 365 }[span];
    api.get(`/admin/analytics/funnel?span=${span}`).then((r) => setFunnel(r.data)).catch(() => setFunnel({ error: true }));
    api.get(`/admin/analytics/revenue?days=${days}`).then((r) => setRev(r.data)).catch(() => setRev({ error: true }));
    api.get(`/admin/analytics/overview?span=${span}`).then((r) => setOv(r.data)).catch(() => setOv(null));
  }, [span]);

  const steps = funnel?.steps || [];
  const top = steps[0]?.n || 1;

  return (
    <div data-testid="admin-growth" className="space-y-5">
      <div className="flex gap-1 flex-wrap" data-testid="growth-span">
        {SPANS.map(([k, l]) => <button key={k} onClick={() => setSpan(k)} data-testid={`growth-span-${k}`} className={`ss-pill h-9 ${span === k ? "ss-pill-primary" : "ss-pill-ghost"}`}>{l}</button>)}
      </div>

      {rev && !rev.error && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label="Estimated MRR" value={money(rev.mrr_usd)} sub={`≈ ${money(rev.arr_usd)} ARR · list prices`} testId="growth-mrr" />
          <Card label={`AI spend · ${rev.days}d`} value={money(rev.ai_cost_usd)} sub={`${rev.ai_calls.toLocaleString()} model calls`} testId="growth-ai-cost" />
          <Card label="AI cost vs revenue" value={rev.ai_cost_pct_of_revenue == null ? "—" : `${rev.ai_cost_pct_of_revenue}%`} sub={`of ${money(rev.revenue_window_usd)} revenue in window`} testId="growth-ai-pct" />
          <Card label="Manual overrides" value={rev.manual_overrides} sub={`${rev.beta_forever} beta / free-forever`} testId="growth-overrides" />
        </div>
      )}

      <AdminVoiceInsights />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ss-card p-5" data-testid="growth-funnel">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Conversion funnel · {span}</p>
          <div className="mt-3 space-y-2.5">
            {steps.map((s) => (
              <div key={s.key} data-testid={`funnel-step-${s.key}`}>
                <div className="flex justify-between text-sm text-cocoa"><span>{s.label}</span><span className="font-medium">{s.n.toLocaleString()}{s.conv != null && <span className="text-cocoa/50 text-xs ml-2">{s.conv}%</span>}</span></div>
                <div className="mt-1 h-2.5 rounded-full bg-champagne/70 overflow-hidden"><div className="h-full bg-cocoa/80" style={{ width: `${Math.max(1, (100 * s.n) / top)}%` }} /></div>
              </div>
            ))}
            {funnel?.error && <p className="text-sm text-rose">Couldn't load funnel.</p>}
          </div>
          <p className="mt-3 text-xs text-cocoa/50">Percentages are of signups (visit → signup is of visitors). Visits count anonymous sessions; later steps count distinct members who signed up in this window.</p>
        </div>

        <div className="ss-card p-5" data-testid="growth-lifecycle">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Member lifecycle · all time</p>
          {funnel?.lifecycle && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[["members", "Members"], ["active_30d", "Active · 30d"], ["with_profile", "With a profile"], ["calibrated", "Calibrated"], ["ever_asked", "Ever asked advisor"], ["paid", "On a paid plan"], ["disabled", "Deactivated"]].map(([k, l]) => (
                <div key={k} className="bg-ivory border border-champagne/70 rounded-lg p-3"><p className="text-[10px] uppercase tracking-widest text-cocoa/50">{l}</p><p className="font-heading text-2xl text-cocoa">{funnel.lifecycle[k]?.toLocaleString()}</p></div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="ss-card p-5" data-testid="growth-plan-mix">
          <p className="text-[10px] uppercase tracking-widest text-cocoa/50">Plan mix & revenue</p>
          <table className="mt-3 w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-cocoa/50"><tr><th className="text-left py-1">Plan</th><th className="text-right py-1">Members</th><th className="text-right py-1">$/mo</th><th className="text-right py-1">MRR</th></tr></thead>
            <tbody>{(rev?.plan_mix || []).map((r) => (
              <tr key={`${r.plan}-${r.canonical}`} className="border-t border-champagne/60 text-cocoa"><td className="py-1.5">{r.label}{r.plan !== r.canonical && <span className="text-xs text-cocoa/45 ml-1">({r.plan})</span>}</td><td className="text-right">{r.members}</td><td className="text-right">{money(r.price_monthly_usd)}</td><td className="text-right font-medium">{money(r.mrr_usd)}</td></tr>
            ))}</tbody>
          </table>
          <p className="mt-3 text-xs text-cocoa/50">{rev?.note}</p>
        </div>
        <div className="space-y-4">
          <div className="ss-card p-5"><p className="text-[10px] uppercase tracking-widest text-cocoa/50">Signups per day</p><div className="mt-3"><Bars rows={ov?.series?.signups_by_day || []} /></div></div>
          <div className="ss-card p-5"><p className="text-[10px] uppercase tracking-widest text-cocoa/50">Upgrades & overrides per day</p><div className="mt-3"><Bars rows={rev?.upgrades_by_day || []} color="bg-sage" /></div></div>
        </div>
      </div>
    </div>
  );
}
